import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";

import { usePdfDocument, usePdfPageCanvas } from "utils";

import { SourceConfidence, SourceLocation } from "models";

import { Segmented, Spin, Typography, buildSourcePageTabs, toSourceBoxStyle } from "components";

const TAB_LABEL: Record<NonNullable<SourceLocation["section"]> | "page", string> = { page: "Page", parcel: "Parcel row, p.", crop: "Crop row, p." };

const confidenceText = (confidence: SourceConfidence, rowCount: number) =>
  ({
    high: "Row found",
    multi: `This parcel is listed on ${rowCount} rows`,
    low: "Possible row, check it",
    page_only: "Row not found",
  })[confidence];

const tabLabel = (page: number, section: SourceLocation["section"]) => `${TAB_LABEL[section ?? "page"]} ${page}`;

type SourcePageViewerProps = {
  /** URL of the source PDF, e.g. a presigned S3 URL. The server must allow range requests from the browser. */
  pdfUrl: string;
  locations: SourceLocation[];
  /** Scrolls the first matched row into view after each render of the page. */
  shouldScrollToRow?: boolean;
};

/** Renders the source PDF page of a doc-scan field and frames the rows and cells it was read from. */
export const SourcePageViewer = ({ pdfUrl, locations, shouldScrollToRow = true }: SourcePageViewerProps) => {
  const tabs = useMemo(() => buildSourcePageTabs(locations), [locations]);
  const [activeTabKey, setActiveTabKey] = useState(tabs[0]?.key);
  const activeTab = tabs.find((tab) => tab.key === activeTabKey) ?? tabs[0];

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const firstRowRef = useRef<HTMLDivElement>(null);
  const { pdf, hasError: hasLoadError } = usePdfDocument(pdfUrl);
  const { isRendering, hasError: hasRenderError } = usePdfPageCanvas({ pdf, pageNumber: activeTab?.page, canvasRef });

  useEffect(() => {
    if (shouldScrollToRow && !isRendering) firstRowRef.current?.scrollIntoView?.({ block: "center" });
  }, [shouldScrollToRow, isRendering, activeTabKey]);

  if (!activeTab) return null;
  if (hasLoadError || hasRenderError) return <Typography variant="paragraph-m">The document could not be loaded.</Typography>;

  return (
    <div className="source-page-viewer">
      <div className="source-page-viewer-header">
        {tabs.length > 1 ? (
          <Segmented<string>
            size="small"
            value={activeTab.key}
            onChange={setActiveTabKey}
            options={tabs.map(({ key, page, section }) => ({ value: key, label: tabLabel(page, section) }))}
          />
        ) : (
          <Typography variant="paragraph-s" bold>
            {tabLabel(activeTab.page, activeTab.section)}
          </Typography>
        )}
        <Typography variant="paragraph-s" color="grey-600">
          {confidenceText(activeTab.confidence, activeTab.locations.length)}
        </Typography>
      </div>
      <div className="source-page-viewer-frame">
        <Spin spinning={isRendering}>
          <div className="source-page-viewer-page">
            <canvas ref={canvasRef} />
            {!isRendering &&
              activeTab.locations.map(
                ({ row, cells, confidence }, index) =>
                  row && (
                    <Fragment key={`${row.x0}-${row.y0}`}>
                      <div
                        ref={index === 0 ? firstRowRef : undefined}
                        className={`source-page-viewer-row ${confidence === "low" ? "source-page-viewer-row-dashed" : ""}`}
                        style={toSourceBoxStyle(row)}
                      />
                      {Object.entries(cells).map(([attribute, cell]) => (
                        <div key={attribute} title={attribute} className="source-page-viewer-cell" style={toSourceBoxStyle(cell)} />
                      ))}
                    </Fragment>
                  )
              )}
          </div>
        </Spin>
      </div>
    </div>
  );
};
